import type { CreateSpaceRequest, SpaceWeeklySchedule } from '../models/Space';
import { OrganizationType } from '../models/Organization';
import { hostApiClient } from '../hostApiClient';
import type { PlaceAddressDraft } from './placeAddress';

/** Facility/school orgs (not individual tutors) manage shared spaces and room booking. */
export function isBusinessTutorOrganization(
  organizationType: OrganizationType | null | undefined
): boolean {
  return (
    organizationType != null && organizationType !== OrganizationType.INDIVIDUAL_TUTOR
  );
}

const defaultWeeklySchedule: SpaceWeeklySchedule = {
  monday: { periodStart: '09:00', periodEnd: '17:00', openAllDay: false, dayOff: false },
  tuesday: { periodStart: '09:00', periodEnd: '17:00', openAllDay: false, dayOff: false },
  wednesday: { periodStart: '09:00', periodEnd: '17:00', openAllDay: false, dayOff: false },
  thursday: { periodStart: '09:00', periodEnd: '17:00', openAllDay: false, dayOff: false },
  friday: { periodStart: '09:00', periodEnd: '17:00', openAllDay: false, dayOff: false },
  saturday: { periodStart: '', periodEnd: '', openAllDay: false, dayOff: true },
  sunday: { periodStart: '', periodEnd: '', openAllDay: false, dayOff: true },
};

export function buildCreateSpaceRequestFromPlaceAddressDraft(
  draft: PlaceAddressDraft,
  spaceName: string
): CreateSpaceRequest {
  const countryCode = draft.countryCode.trim() || 'PL';
  return {
    name: spaceName.trim() || 'Lesson location',
    description: spaceName.trim() || 'Tutor lesson location',
    imageUrls: [],
    howToFindVideoUrls: [],
    googlePlaceId: '',
    googleMapsUrl: draft.googleMapsUrl.trim(),
    addressLine1: draft.addressLine1.trim(),
    addressLine2: draft.addressLine2.trim(),
    latitude: draft.latitude ?? 0,
    longitude: draft.longitude ?? 0,
    city: draft.city.trim(),
    state: draft.state.trim(),
    zipCode: draft.zipCode.trim(),
    countryCode,
    weeklySchedule: defaultWeeklySchedule,
    amenities: [],
    gears: [],
    currencyCode: 'PLN',
  };
}

/** Resolves tutor lesson `spaceId` from listing, org spaces, or a new host space. */
export async function ensureLessonLocationSpaceId(
  currentSpaceId: string | null | undefined,
  draft: PlaceAddressDraft,
  spaceName: string
): Promise<string> {
  if (currentSpaceId?.trim()) {
    return currentSpaceId.trim();
  }

  const spaces = await hostApiClient.getSpaces();
  if (spaces.length === 1) {
    const onlySpaceId = spaces[0].id?.trim();
    if (onlySpaceId) {
      return onlySpaceId;
    }
  }

  const created = await hostApiClient.createSpace(
    buildCreateSpaceRequestFromPlaceAddressDraft(draft, spaceName)
  );
  return String(created.id);
}
